
import EventEmitter from 'events';
import Video, { AudioTrack, Participant, RemoteParticipant, RemoteTrack, RemoteTrackPublication, Room as TwilioRoom, TwilioError, VideoTrack } from 'twilio-video';
import { Room } from '../models/Room';
import { JoinedOrCreated, NotifyUsersOnNewVoiceCall } from '../models/local/Types';
import { Fire } from './FireClient';
import { My } from './My';
import { ScreensharingHelper } from './ScreensharingHelper';
import { KeepDeviceAwake } from './Utils/KeepDeviceAwake';
import { SoundUtil } from './Utils/SoundUtil';
import { UtilApp } from './Utils/UtilApp';


export class TwilioHelpEvent {
  constructor(public type: 'StateChange' | 'UserMuteChanged' | 'DominantSpeakerChanged',
    public userId?: string, public value?: boolean, public error?: string) {
  }
}

export declare interface TwilioHelper {
  on(event: 'Update', listener: (e: TwilioHelpEvent) => void): this;
}

export class TwilioHelper extends EventEmitter {
  participantSpans = new Map<string, HTMLSpanElement>(); // string is userId (identity)
  remoteParticipants = new Map<string, RemoteParticipant>(); // string is userId (identity) 
  twilioRoom: TwilioRoom | undefined;
  isConnecting = false;
  audioInputLabel?: string;
  screenSharingHelper = new ScreensharingHelper();
  subscribeToTwilioUpdates(handler: (e: TwilioHelpEvent | null) => void): () => void {
    twil.on('Update', handler);
    return () => twil.removeListener('Update', handler)
  }

  async addLocalVideo(html: HTMLDivElement) {
    const track = await Video.createLocalVideoTrack()
    html.appendChild(track.attach());
  }

  addParticipantSpan(userId: string): HTMLSpanElement {
    const partEl = document.createElement('span');
    partEl.setAttribute('id', userId);
    this.participantSpans.set(userId, partEl);
    return partEl;
  }

  async join(room: Room, identity: string): Promise<JoinedOrCreated> {
    let res = await this.connect(room, identity, null);
    this.removeDisconnectedUsersFromRoomCall();
    return res;
  }

  async connect(room: Room, identity: string, notify: NotifyUsersOnNewVoiceCall): Promise<JoinedOrCreated> {
    if (this.twilioRoom?.state == 'connected') {
      this.leaveRoom();
    }
    this.isConnecting = true;
    this.emitUpdate(null);

    let token = await Fire.call.getRoomAndToken(identity, room.id);
    let enableVideoForIPadAppWorkaround = UtilApp.isIPadApp();
    
    this.twilioRoom = await Video.connect(token, { video: enableVideoForIPadAppWorkaround, audio: true, dominantSpeaker: true });
    if (enableVideoForIPadAppWorkaround) {
      this.twilioRoom.localParticipant.videoTracks.forEach(vtrack => {
        vtrack.unpublish();
        vtrack.track.stop();
      })
    }
    this.updateDefaultDevices();

    // console.log('this.twilioRoom.localParticipant.audioTracks; is', this.twilioRoom.localParticipant.audioTracks);

    this.participantSpans.clear();
    this.remoteParticipants.clear();
    this.addParticipantSpan(identity);

    let partConnectedHandler = (participant: RemoteParticipant) => {
      const participantSpan = this.addParticipantSpan(participant.identity);
      this.remoteParticipants.set(participant.identity, participant);

      const trackSubscribed = (span: HTMLElement, track: RemoteTrack) => {
        // div.appendChild((track as AudioTrack).attach());
        if (track.kind == 'audio') {
          span.appendChild((track as AudioTrack).attach());
        } else {
          span.appendChild((track as VideoTrack).attach());
        }
        console.log('Track attached', track.kind);
        this.emitUpdate(null);
      };

      const trackUnsubscribed = (track: AudioTrack) => {
        track.detach().forEach(element => {
          element.remove()
        });
      };

      participant.tracks.forEach((publication: RemoteTrackPublication) => {
        if (publication.isSubscribed && publication.track) {
          trackSubscribed(participantSpan, publication.track);
        }
      });

      participant.on('trackSubscribed', track => trackSubscribed(participantSpan, track));
      participant.on('trackUnsubscribed', trackUnsubscribed);
      participant.on('trackEnabled', () => this.emitUpdate(new TwilioHelpEvent('UserMuteChanged', participant.identity, false)));
      participant.on('trackDisabled', () => this.emitUpdate(new TwilioHelpEvent('UserMuteChanged', participant.identity, true)));

      this.emitUpdate(null);
      SoundUtil.playJoined();
    }

    let partDisconnectedHandler = (participant: Participant) => {
      this.participantSpans.delete(participant.identity); // = this.participantSpans.filter(e => e.id != participant.identity);
      this.remoteParticipants.delete(participant.identity);

      this.emitUpdate(null);
      SoundUtil.playLeft();
    }

    let disconnected = (room: TwilioRoom, error: TwilioError) => {
      if (error) {
        console.warn("Error is", error);
        this.twilioRoom = undefined;
        this.emitUpdate(new TwilioHelpEvent('StateChange', undefined, undefined, error.message));
      }
      this.stopScreenSharing();
      KeepDeviceAwake.allowSleep();
    }

    this.twilioRoom.participants.forEach(partConnectedHandler);
    this.twilioRoom.on('participantConnected', partConnectedHandler);
    this.twilioRoom.on('participantDisconnected', partDisconnectedHandler);
    this.twilioRoom.on('disconnected', disconnected);
    this.twilioRoom.on('dominantSpeakerChanged', (speaker: RemoteParticipant) => this.emitUpdate(new TwilioHelpEvent('DominantSpeakerChanged', speaker?.identity)))

    this.isConnecting = false;
    this.emitUpdate(null);
    KeepDeviceAwake.keepAwake();
    return Fire.call.createOrJoinRoomCall(room, My.user, notify);
  }

  async leaveRoom() {

    if (this.getRoomId()) {
      Fire.call.leaveRoomCall(this.getRoomId(), My.user.id);
      this.twilioRoom?.disconnect();
      this.twilioRoom = undefined;
      this.audioInputLabel = undefined;
    }
    this.stopScreenSharing();
    this.emitUpdate(null);
  }

  isConnected() {
    return this.twilioRoom?.state == 'connected';
  }

  getRoomId() {
    return this.twilioRoom?.name ?? "";
  }

  async removeDisconnectedUsersFromRoomCall() {
    if (this.participantSpans.size == 1) {
      // Alone in the call.
      const roomCall = await Fire.call.getRoomCall(this.getRoomId());
      if (roomCall?.users.filter(u => u.id != My.user.id).length > 0) {
        console.log('Removing disconnected users from room call');
        roomCall.userIds = [My.user.id];
        roomCall.users = [My.user];
        Fire.call.updateRoomCall(roomCall);
      }
    }
  }

  enableLocalMicrophone(value: boolean) {
    let tracks = this.twilioRoom?.localParticipant.audioTracks;
    if (tracks && tracks.size > 0) {
      const localAudioTrack = Array.from(tracks.values())[0];
      localAudioTrack.track.enable(value);
      this.emitUpdate(null);
    } else {
      console.error("TwilioHelper.enableLocalMicrophone: Could not find a local audio track.")
    }
  }

  isMicEnabled(): boolean {
    let tracks = this.twilioRoom?.localParticipant.audioTracks;
    if (tracks && tracks.size > 0) {
      const localAudioTrack = Array.from(tracks.values())[0];
      return localAudioTrack.track.isEnabled;
    } 
    return false;
  }

  emitUpdate(e: TwilioHelpEvent | null) {
    this.emit('Update', e ?? new TwilioHelpEvent('StateChange'));
  }

  updateDefaultDevices() {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        this.audioInputLabel = devices.find(device => device.kind === 'audioinput' && device.deviceId === 'default')?.label;
      });
    }
  }

  startScreenSharing(onStop: () => void) {
    if (this.twilioRoom) {
      return this.screenSharingHelper.startSharing(this.twilioRoom, onStop)
    }
  }

  stopScreenSharing() {
    this.screenSharingHelper.stopSharing(this.twilioRoom);
  }

  isScreenSharing() {
    return this.screenSharingHelper.isSharing();
  }
}

export var twil = new TwilioHelper();
